/**
 * §7.5 前端資料層。取代 mockup 的 `state/StateContext`。
 *
 * mockup 是「一份 events 陣列 + 本地推導」；正式站台改為各畫面呼叫查詢 API 拿投影後的 DTO。
 *
 * 這個檔案負責四件事，個別畫面不必再想：
 *
 *   §5.5  所有變更操作帶 `X-Requested-With: fetch`（CSRF）
 *   §10.3 作業 API 帶 `Idempotency-Key`，**重試時沿用同一個 key**
 *   §10.4 Session 過期 → 跳登入對話框，**不離開目前畫面**，登入成功後直接重送
 *   §8.6  錯誤格式：`message` 直接顯示給使用者
 */

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> }
}

/** §8.6 後端回的錯誤。`message` 是給人看的繁中，直接顯示就好。 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, unknown>

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.error.code
    this.details = body.error.details
  }
}

/** 連線斷掉（現場 wifi）。與伺服器回錯不同，這個要保留表單內容讓人重試（§10.4）。 */
export class NetworkError extends Error {
  constructor(readonly cause: unknown) {
    super('連線中斷，內容已保留。請確認網路後重試。')
    this.name = 'NetworkError'
  }
}

// ── §10.4 Session 過期的處理 ───────────────────────────────────────────────
// 過期時**不能**直接跳走：現場可能正在輸入第 180 片的重量。
// 所以這裡把請求掛起、通知 UI 開登入對話框，登入成功後把掛起的請求重送。

type ReauthHandler = () => Promise<boolean>
let reauthHandler: ReauthHandler | null = null
let reauthInFlight: Promise<boolean> | null = null

export function setReauthHandler(h: ReauthHandler | null): void {
  reauthHandler = h
}

async function requestReauth(): Promise<boolean> {
  if (!reauthHandler) return false
  // 同時有十個請求撞到 401 時，只開一個對話框
  reauthInFlight ??= reauthHandler().finally(() => { reauthInFlight = null })
  return reauthInFlight
}

// ── 請求 ───────────────────────────────────────────────────────────────────

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export interface RequestOptions {
  /** §10.3 作業 API 用。重試時務必沿用同一個值 */
  idempotencyKey?: string
  /** 不要在 401 時嘗試重新登入（登入相關的 API 自己用） */
  noReauth?: boolean
  signal?: AbortSignal
}

async function raw<T>(
  method: string, path: string, body: unknown, opts: RequestOptions,
): Promise<T> {
  const headers: Record<string, string> = {}
  // §5.5 CSRF：SameSite=Lax + 變更操作要求這個標頭
  if (MUTATING.has(method)) headers['X-Requested-With'] = 'fetch'
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey

  let res: Response
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e
    throw new NetworkError(e)
  }

  if (res.status === 204) return undefined as T

  // 下載類的回應（CSV）不是 JSON
  const type = res.headers.get('content-type') ?? ''
  if (res.ok && !type.includes('application/json')) {
    return (await res.text()) as unknown as T
  }

  let parsed: unknown = null
  const text = await res.text()
  if (text) {
    try { parsed = JSON.parse(text) } catch { parsed = { error: { code: 'BAD_RESPONSE', message: text.slice(0, 300) } } }
  }

  if (res.ok) return parsed as T
  throw new ApiError(res.status, parsed as ApiErrorBody)
}

async function request<T>(
  method: string, path: string, body?: unknown, opts: RequestOptions = {},
): Promise<T> {
  try {
    return await raw<T>(method, path, body, opts)
  } catch (e) {
    // §10.4 Session 過期 → 開登入對話框，成功後**用同一個 idempotencyKey 重送**
    if (e instanceof ApiError && e.status === 401 && e.code === 'UNAUTHENTICATED' && !opts.noReauth) {
      if (await requestReauth()) return raw<T>(method, path, body, opts)
    }
    throw e
  }
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body ?? {}, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body, opts),
  del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
}

/** §8.1 分頁：?limit=&cursor= */
export function withQuery(path: string, params: Record<string, unknown>): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    q.set(k, String(v))
  }
  const s = q.toString()
  return s ? `${path}?${s}` : path
}

/**
 * §11.5 下載。瀏覽器的下載要走真正的導覽，不能用 fetch 再自己組 blob——
 * 那樣會失去 Content-Disposition 的檔名，而且大檔（串流）會先全部進記憶體。
 */
export function download(path: string, params: Record<string, unknown> = {}): void {
  window.location.href = withQuery(path, params)
}

/** §10.3 一次操作一個 key。重試時沿用同一個。 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}
